import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, type AssistantObservation } from '../api';
import { ResponsiveList } from '../components/ResponsiveList';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SmsConversation, SmsInbox } from '../components/SmsChat';
import { Phone } from '../components/Phone';
import { clampPage } from '../lib/clampPage';

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 44,
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  marginBottom: 'var(--spacing-2)',
};
const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  minHeight: 44,
};
const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  minHeight: 44,
};
const dangerButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'rgba(220,53,69,0.12)',
  border: '1px solid rgba(220,53,69,0.25)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-error)',
  fontWeight: 600,
  minHeight: 44,
};

function badge(text: string, color: string, bg: string): React.ReactNode {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        color,
        backgroundColor: bg,
        borderRadius: 4,
        padding: '1px 6px',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

// Small "‹ Prev / Page X of Y / Next ›" pagination control style, matching
// the existing convention already used (independently, not extracted into a
// shared component) in Messaging.tsx's contact/log/message list pagination.
const smallButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  borderRadius: 'var(--radius-default)',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface-elevated)',
  color: 'var(--color-text)',
  cursor: 'pointer',
};

// Contacts → Online (Pending/Dismissed Quick Message lists) — server-side
// pagination via listAssistantObservationsPaginated (category='quick_message',
// filtered in SQL before LIMIT/OFFSET on the backend — no 200-row ceiling,
// unlike the legacy listAssistantObservations feed Billing/Assistant/the
// sidebar quote-request badge still share).
const ONLINE_MESSAGES_PAGE_SIZE = 15;

const EMPTY = { full_name: '', email: '', mobile: '', company: '', address_1: '', suburb: '' };

export function Contacts() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'reply' | 'contacts' | 'messages' | 'phone' | 'online'>('reply');
  const [onlineSubView, setOnlineSubView] = useState<'pending' | 'dismissed'>('pending');
  // Holds the actual selected row (captured at the moment "Open" is
  // clicked), not just an id — the item lives on whichever server page is
  // currently loaded, and there is no longer a full unpaginated array to
  // re-search it in on every render (see listAssistantObservationsPaginated).
  const [openOnlineMessage, setOpenOnlineMessage] = useState<AssistantObservation | null>(null);
  const [phonePrefill, setPhonePrefill] = useState<string | undefined>(undefined);
  const [detailId, setDetailId] = useState<number | null>(null); // Details (fields/edit/delete) modal
  const [chatId, setChatId] = useState<number | null>(null); // Chat (notes/stories) modal
  const [editing, setEditing] = useState<any | null>(null); // form data when adding/editing
  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [xeroLinkFor, setXeroLinkFor] = useState<{ id: number; name: string } | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<{ id: number; name: string } | null>(null);

  // Only one of the two modals is open at a time; share a single detail query.
  const activeId = detailId ?? chatId;
  const openDetails = (id: number) => {
    setChatId(null);
    setDetailId(id);
  };
  const openChat = (id: number) => {
    setDetailId(null);
    setChatId(id);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', search],
    queryFn: () => api.listContacts({ search: search || undefined, limit: 100 }),
  });
  const { data: detail } = useQuery({
    queryKey: ['contact', activeId],
    queryFn: () => api.getCustomerRecord(activeId as number),
    enabled: activeId != null,
  });

  // "To reply" queue: contacts with unread received SMS (one per conversation,
  // newest first). Reply opens their thread; Mark read clears it. Driven straight
  // off the message threads so the count always matches what's actually unread.
  const { data: smsThreadsData } = useQuery({
    queryKey: ['sms-threads'],
    queryFn: () => api.listSmsThreads(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
  const toReply = useMemo(() => {
    const threads = (smsThreadsData?.threads ?? []) as any[];
    return threads
      .filter((t) => (t.unread ?? 0) > 0)
      .map((t) => ({
        key: t.customerId != null ? `c${t.customerId}` : `p:${t.phone}`,
        customerId: (t.customerId ?? null) as number | null,
        phone: t.phone as string,
        name: (t.customerName ?? t.phone) as string,
        snippet: (t.lastBody || '(no text — likely a photo/attachment)') as string,
        at: (t.lastAt ?? null) as string | null,
        unread: (t.unread ?? 0) as number,
      }))
      .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());
  }, [smsThreadsData]);
  const replyCount = toReply.length;
  const unreadTotal = toReply.reduce((n, r) => n + r.unread, 0);
  const markRead = useMutation({
    mutationFn: (phone: string) => api.markSmsThreadRead(phone),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sms-threads'] }),
  });

  // Independent page state per sub-view — switching Pending↔Dismissed, or
  // opening/closing the detail modal, must never reset either one. 1-indexed
  // to match the "Page X of Y" label directly. Declared before the queries
  // below since both query keys/params depend on the current page.
  const [pendingPage, setPendingPage] = useState(1);
  const [dismissedPage, setDismissedPage] = useState(1);

  // "Online" (Contacts tab): website Contact-page Quick Message submissions.
  // Server-paginated AND server-category-filtered — category='quick_message'
  // filtering happens in SQL, before LIMIT/OFFSET (see
  // assistant.ts#listObservationsPaginated) — so, unlike the legacy
  // listAssistantObservations feed Billing/Assistant/the sidebar
  // quote-request badge still share, there is no 200-row-across-all-
  // categories ceiling here: `total`/`totalPages` reflect every matching
  // row in the table, however many pages that is.
  const { data: pendingData } = useQuery({
    queryKey: ['assistant-observations', 'quick_message', 'new', pendingPage, ONLINE_MESSAGES_PAGE_SIZE],
    queryFn: () =>
      api.listAssistantObservationsPaginated({ status: 'new', category: 'quick_message', page: pendingPage, pageSize: ONLINE_MESSAGES_PAGE_SIZE }),
    refetchInterval: 30_000,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });
  const onlineMessages = pendingData?.items ?? [];
  const pendingTotal = pendingData?.total ?? 0;
  const pendingTotalPages = pendingData?.totalPages ?? 1;

  // Only fetched once the admin actually opens the Dismissed sub-view —
  // there's no live-notification need for archived items, so no polling.
  const { data: dismissedData } = useQuery({
    queryKey: ['assistant-observations', 'quick_message', 'archived', dismissedPage, ONLINE_MESSAGES_PAGE_SIZE],
    queryFn: () =>
      api.listAssistantObservationsPaginated({ status: 'archived', category: 'quick_message', page: dismissedPage, pageSize: ONLINE_MESSAGES_PAGE_SIZE }),
    enabled: view === 'online' && onlineSubView === 'dismissed',
  });
  const onlineDismissed = dismissedData?.items ?? [];
  const dismissedTotal = dismissedData?.total ?? 0;
  const dismissedTotalPages = dismissedData?.totalPages ?? 1;

  // Clamp after a dismissal/refetch shrinks the count out from under the
  // current page (e.g. dismissing the only item on page 3 of 3 → 2 pages —
  // this brings the page back to 2 instead of rendering "Page 3 of 2" or an
  // empty page). A no-op whenever the count grows/stays the same size (a new
  // incoming message never forces the page back to 1). totalPages now comes
  // from the server on every poll/refetch, not a client-computed value.
  useEffect(() => {
    setPendingPage((p) => clampPage(p, pendingTotalPages));
  }, [pendingTotalPages]);
  useEffect(() => {
    setDismissedPage((p) => clampPage(p, dismissedTotalPages));
  }, [dismissedTotalPages]);

  const dismissOnlineMessage = useMutation({
    mutationFn: (id: number) => api.archiveAssistantObservation(id),
    onSuccess: () => {
      setOpenOnlineMessage(null);
      // Invalidates every ['assistant-observations', ...] query — both the
      // new paginated pending/dismissed/badge queries above (so the page
      // that just lost an item, the dismissed history, and the sidebar
      // count all refetch) and the legacy shared query Billing/Assistant
      // use (harmless — those simply refetch data that didn't change).
      queryClient.invalidateQueries({ queryKey: ['assistant-observations'] });
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
    if (activeId) queryClient.invalidateQueries({ queryKey: ['contact', activeId] });
  };
  const saveMutation = useMutation({
    mutationFn: (form: any) =>
      form.id ? api.updateCustomerRecord(form.id, form) : api.createCustomerRecord(form),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  // Merge a duplicate contact into the one being edited (same person, shared mobile).
  const mergeMutation = useMutation({
    mutationFn: (v: { keepId: number; mergeId: number }) => api.mergeCustomerRecord(v.keepId, v.mergeId),
    onSuccess: (rec: any) => {
      saveMutation.reset();
      // The merge pulls the duplicate's mobile onto the survivor, so the number
      // the user was adding is now on this record. Show the consolidated contact.
      setEditing(rec || null);
      invalidate();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteCustomerRecord(id),
    onSuccess: () => {
      setPendingDelete(null);
      setDetailId(null);
      invalidate();
    },
  });
  const [pendingRemove, setPendingRemove] = useState<{ id: number; name: string } | null>(null);
  const removeMutation = useMutation({
    mutationFn: (id: number) => api.removeContact(id),
    onSuccess: () => {
      setPendingRemove(null);
      invalidate(); // keep the modal open so the "removed" banner + Restore show
    },
  });
  const restoreMutation = useMutation({
    mutationFn: (id: number) => api.restoreContact(id),
    onSuccess: () => invalidate(),
  });
  // Toggle email/SMS subscription directly from the contact detail.
  const toggleSub = useMutation({
    mutationFn: (v: { id: number; channel: 'email' | 'sms'; optedOut: boolean }) =>
      api.setContactOptout(v.id, v.channel, v.optedOut),
    onSuccess: () => invalidate(),
  });
  // Unlink this contact from its Xero contact (clears xero_contact_id).
  const unlinkXero = useMutation({
    mutationFn: (id: number) => api.linkContactXero(id, null),
    onSuccess: () => {
      setPendingUnlink(null);
      invalidate();
    },
  });
  // Per-contact arrival-reminder channel: Auto (SMS, email if no mobile) / SMS / Email.
  const setArrivalChannel = useMutation({
    mutationFn: (v: { id: number; channel: 'sms' | 'email' | null }) => api.setArrivalChannel(v.id, v.channel),
    onSuccess: () => invalidate(),
  });
  // Add this contact to the referral partners list (e.g. an agent who sends work).
  const [partnerNote, setPartnerNote] = useState<string | null>(null);
  const addAsPartner = useMutation({
    mutationFn: (c: { name: string; email: string | null; mobile: string | null }) =>
      api.createReferralPartner({
        name: c.name,
        relationship_type: 'Active',
        contacts: c.email || c.mobile ? [{ name: c.name, phone: c.mobile, email: c.email }] : [],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-partners'] });
      setPartnerNote('✓ Added to referral partners.');
      setTimeout(() => setPartnerNote(null), 3000);
    },
  });
  // Removal-request review queue (replies the scanner flagged but didn't auto-remove)
  const { data: reviews } = useQuery({ queryKey: ['removal-reviews'], queryFn: () => api.listRemovalReviews() });
  const [rejectNote, setRejectNote] = useState<{ id: number; text: string } | null>(null);
  const resolveReview = useMutation({
    mutationFn: (v: { id: number; action?: 'remove' | 'none'; verdict?: 'good' | 'bad'; note?: string }) =>
      api.resolveRemovalReview(v.id, { action: v.action, verdict: v.verdict, note: v.note }),
    onSuccess: () => {
      setRejectNote(null);
      queryClient.invalidateQueries({ queryKey: ['removal-reviews'] });
      invalidate();
    },
  });
  // Unified action queue count: drafted replies + scanner review items.
  const actionCount = replyCount + (reviews?.length ?? 0);

  // Per-contact chat composer (text + images)
  const [noteText, setNoteText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const sendNoteMutation = useMutation({
    mutationFn: async () => {
      if (!chatId) return;
      const attachmentIds: number[] = [];
      for (const file of files) {
        const dataBase64 = await fileToBase64(file);
        const r = await api.uploadAttachment(chatId, {
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          dataBase64,
        });
        attachmentIds.push(r.id);
      }
      return api.addCustomerNote(chatId, { message: noteText, attachmentIds });
    },
    onSuccess: () => {
      setNoteText('');
      setFiles([]);
      invalidate();
    },
  });

  // Per-contact assistant conversation (grounded in business knowledge + this customer).
  type ContextUsed = {
    knowledgeBase: string[];
    customer: { name: string | null; counts: { quotes: number; invoices: number; bookings: number; notes: number } } | null;
  };
  type Turn = { role: 'user' | 'assistant'; content: string; contextUsed?: ContextUsed };
  const [convo, setConvo] = useState<Turn[]>([]);
  const [understanding, setUnderstanding] = useState<Awaited<ReturnType<typeof api.understandMessage>> | null>(null);
  // Triage hand-off: /contacts?chat=<id>&draft=<prompt> opens that contact's
  // assistant pre-filled to draft a reply. Read once on mount, then clear.
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingDraftRef = useRef<string | null>(null);
  const pendingOpenRef = useRef(false); // auto-open the single match of a ?q deep link
  const pendingXeroOpenRef = useRef<string | null>(null); // auto-open the contact with this Xero id
  useEffect(() => {
    const chat = searchParams.get('chat');
    const open = searchParams.get('open');
    const xeroContact = searchParams.get('xeroContact');
    const q = searchParams.get('q');
    if (chat) {
      pendingDraftRef.current = searchParams.get('draft');
      setChatId(Number(chat));
      setSearchParams({}, { replace: true });
    } else if (open) {
      // Deep link from the Assistant: open this contact's detail directly.
      setDetailId(Number(open));
      setSearchParams({}, { replace: true });
    } else if (xeroContact) {
      // Deep link from Billing (clicking a contact name): open the exact contact
      // by its Xero id. The name loads the list so we have a row to open from.
      setView('contacts');
      if (q) setSearch(q);
      pendingXeroOpenRef.current = xeroContact;
      setSearchParams({}, { replace: true });
    } else if (q) {
      // Deep link from Quotes: search for the contact and open it if there's one match.
      setView('contacts');
      setSearch(q);
      pendingOpenRef.current = true;
      setSearchParams({}, { replace: true });
    }
    // run once on mount to pick up ?chat/?draft/?open/?xeroContact/?q hand-off params
  }, []);
  // Load the persisted conversation when switching to a contact (the agent's
  // memory). Falls back to empty if none / not yet loaded.
  useEffect(() => {
    setNoteText(pendingDraftRef.current || '');
    pendingDraftRef.current = null;
    setFiles([]);
    setUnderstanding(null);
    if (!chatId) {
      setConvo([]);
      return;
    }
    setConvo([]);
    let cancelled = false;
    api
      .getCustomerConversation(chatId)
      .then((turns) => {
        if (!cancelled) setConvo(turns.map((t) => ({ role: t.role, content: t.content })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chatId]);
  const askMutation = useMutation({
    mutationFn: async (question: string) => {
      if (!chatId) throw new Error('No contact');
      return api.customerAssistant(chatId, { message: question });
    },
    onSuccess: (res) =>
      setConvo((prev) => [...prev, { role: 'assistant', content: res.reply, contextUsed: res.contextUsed }]),
    onError: (e: any) =>
      setConvo((prev) => [...prev, { role: 'assistant', content: `⚠️ ${e.message}` }]),
  });
  // 👍/👎 feedback on assistant chat answers (teaches it for next time).
  const [chatRated, setChatRated] = useState<Record<number, 'good' | 'bad'>>({});
  const [chatReject, setChatReject] = useState<{ i: number; text: string } | null>(null);
  const chatFbMut = useMutation({
    mutationFn: (v: { i: number; verdict: 'good' | 'bad'; question: string; answer: string; note?: string }) =>
      api.assistantChatFeedback(chatId as number, { verdict: v.verdict, question: v.question, answer: v.answer, note: v.note }),
    onSuccess: (_r, v) => { setChatRated((m) => ({ ...m, [v.i]: v.verdict })); setChatReject(null); },
  });
  // Every Send also "understands" the message — surfaces the agent's read
  // (intent, quote references, addresses) inline alongside the drafted reply.
  const understandMut = useMutation({
    mutationFn: (msg: string) => api.understandMessage(msg),
    onSuccess: (r) => setUnderstanding(r),
  });
  const askAssistant = () => {
    const q = noteText.trim();
    if (!q || askMutation.isPending) return;
    setUnderstanding(null);
    understandMut.mutate(q); // understand first, then draft — both off the same message
    setConvo((prev) => [...prev, { role: 'user', content: q }]);
    setNoteText('');
    askMutation.mutate(q);
  };

  const contacts = data?.contacts ?? [];

  // After a ?q deep link (from Quotes) loads its results, open the contact
  // automatically when there's exactly one match; otherwise leave the filtered
  // list for the admin to pick from.
  useEffect(() => {
    if (isLoading) return;
    if (pendingXeroOpenRef.current) {
      const xid = pendingXeroOpenRef.current;
      pendingXeroOpenRef.current = null;
      const match = (contacts as any[]).find((c) => c.xeroContactId === xid);
      if (match) { openDetails(match.id); return; }
      if (contacts.length === 1) { openDetails(contacts[0].id); return; }
      // No local contact for this Xero id (e.g. a no-email Xero contact that the
      // sync never created a record for): create/link one from the Xero contact,
      // then open it, so the quote's contact always opens.
      api.getOrCreateCustomerByXeroContact(xid)
        .then((r) => { if (r?.id) { queryClient.invalidateQueries({ queryKey: ['contacts'] }); openDetails(r.id); } })
        .catch(() => { /* leave the filtered list for a manual pick */ });
      return;
    }
    if (pendingOpenRef.current) {
      if (contacts.length === 1) openDetails(contacts[0].id);
      pendingOpenRef.current = false;
    }
  }, [contacts, isLoading]);

  return (
    <div style={{ padding: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Contacts</h1>
          <p style={{ color: 'var(--color-text-muted)', margin: 'var(--spacing-1) 0 0' }}>
            {data ? `${data.total} contacts` : 'Loading…'} · source-tagged, compared with Xero
          </p>
        </div>
        <button style={primaryButtonStyle} onClick={() => setEditing({ ...EMPTY })}>
          + Add contact
        </button>
      </div>

      {/* Tabs: the unified action queue ("To reply") and all contacts */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        {(['reply', 'contacts', 'messages', 'phone', 'online'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              ...(view === v ? primaryButtonStyle : secondaryButtonStyle),
              ...(v === 'reply' && actionCount > 0 && view !== v ? { border: '1px solid var(--color-accent, #FEC553)' } : {}),
              ...(v === 'online' && pendingTotal > 0 && view !== v ? { border: '1px solid var(--color-accent, #FEC553)' } : {}),
            }}
          >
            {v === 'reply'
              ? `To reply${actionCount ? ` (${actionCount})` : ''}`
              : v === 'contacts'
                ? 'Contacts'
                : v === 'messages'
                  ? '💬 Messages'
                  : v === 'phone'
                    ? '📞 Phone'
                    : `🌐 Online${pendingTotal ? ` (${pendingTotal})` : ''}`}
          </button>
        ))}
      </div>

      {view === 'reply' && (
        <section style={pageCardStyle}>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0, fontSize: 'var(--font-size-sm)' }}>
            Unread text messages waiting for a reply{unreadTotal > 0 ? ` — ${unreadTotal} unread across ${replyCount} conversation${replyCount === 1 ? '' : 's'}` : ''}, plus anything the scanner flagged. Reply, or mark read once handled.
          </p>
          {toReply.length === 0 && (reviews?.length ?? 0) === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Nothing waiting right now. 🎉</p>
          ) : (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
              {toReply.map((r) => (
                <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-3)', borderTop: '1px solid var(--color-border)' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {r.customerId ? (
                        <button onClick={() => openDetails(r.customerId as number)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 700, cursor: 'pointer' }}>{r.name}</button>
                      ) : r.name}
                      <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#fff', background: 'var(--color-error)', borderRadius: 10, padding: '0 6px' }}>{r.unread}</span>
                      {r.at && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: 6 }}>· {fmtWhen(r.at)}</span>}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.snippet}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0 }}>
                    {r.customerId && <button style={primaryButtonStyle} onClick={() => openDetails(r.customerId as number)}>Reply</button>}
                    <button style={secondaryButtonStyle} disabled={markRead.isPending} title="Remove this conversation from the To-reply list" onClick={() => markRead.mutate(r.phone)}>Dismiss</button>
                  </div>
                </div>
              ))}
              {/* Scanner review items (removal-unclear / possible quote) folded into the same queue. */}
              {(reviews ?? []).map((rv: any) => (
                <div key={`rv-${rv.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-3)', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 240, flex: '1 1 320px' }}>
                    <div style={{ fontWeight: 600 }}>
                      {rv.customerId ? (
                        <button onClick={() => openDetails(rv.customerId)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer' }}>{rv.name ?? rv.email ?? 'Unknown contact'}</button>
                      ) : (rv.name ?? rv.email ?? 'Unknown contact')}{' '}
                      {rv.kind === 'quote'
                        ? badge('Possible quote', 'var(--color-accent)', 'var(--color-accent-muted)')
                        : badge('Removal request', 'var(--color-error)', 'rgba(220,53,69,0.12)')}
                      {rv.createdAt && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: 6 }}>· {fmtWhen(rv.createdAt)}</span>}
                    </div>
                    {rv.llmReason && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{rv.llmReason}</div>}
                    {rv.replySnippet && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 2, fontStyle: 'italic' }}>“{rv.replySnippet}”</div>}
                    {rejectNote?.id === rv.id && (
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginTop: 'var(--spacing-1)' }}>
                        <input autoFocus value={rejectNote.text} onChange={(e) => setRejectNote({ id: rv.id, text: e.target.value })} placeholder="Why was this wrong? (helps the scanner learn)" style={{ ...inputStyle, width: 260 }} />
                        <button style={primaryButtonStyle} disabled={resolveReview.isPending || !rejectNote.text.trim()} onClick={() => resolveReview.mutate({ id: rv.id, verdict: 'bad', note: rejectNote.text.trim() })}>Submit</button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {rv.kind === 'quote' ? (
                      <button style={secondaryButtonStyle} onClick={() => rv.customerId && openChat(rv.customerId)}>Open to quote</button>
                    ) : (
                      <button style={secondaryButtonStyle} disabled={resolveReview.isPending} onClick={() => resolveReview.mutate({ id: rv.id, action: 'remove', verdict: 'good' })}>Remove</button>
                    )}
                    <button style={secondaryButtonStyle} title="Good call — classified correctly" disabled={resolveReview.isPending} onClick={() => resolveReview.mutate({ id: rv.id, verdict: 'good' })}>👍 Good</button>
                    <button style={secondaryButtonStyle} title="Wrong — tell the scanner why" onClick={() => setRejectNote(rejectNote?.id === rv.id ? null : { id: rv.id, text: '' })}>👎 Wrong</button>
                    <button style={secondaryButtonStyle} title="No action needed — remove from the list" disabled={resolveReview.isPending} onClick={() => resolveReview.mutate({ id: rv.id, action: 'none' })}>Dismiss</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {view === 'messages' && <SmsInbox onOpenContact={(id) => setDetailId(id)} />}

      {view === 'phone' && (
        <Phone
          initialNumber={phonePrefill}
          onOpenContact={(id) => setDetailId(id)}
          onCreateFromNumber={(num) => setEditing({ ...EMPTY, mobile: num })}
        />
      )}

      {view === 'online' && (
        <section style={pageCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            <p style={{ color: 'var(--color-text-muted)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
              Quick Message submissions from the website Contact page.
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
              {(['pending', 'dismissed'] as const).map((sv) => (
                <button key={sv} onClick={() => setOnlineSubView(sv)} style={onlineSubView === sv ? primaryButtonStyle : secondaryButtonStyle}>
                  {sv === 'pending' ? 'Pending' : 'Dismissed'}
                </button>
              ))}
            </div>
          </div>

          {onlineSubView === 'pending' ? (
            pendingTotal === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No pending messages. 🎉</p>
            ) : (
              <>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
                  {onlineMessages.map((m, i) => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-3)', padding: 'var(--spacing-3)', borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 240, flex: '1 1 320px' }}>
                        <div style={{ fontWeight: 700 }}>
                          {m.fromName ?? 'Unknown'}
                          {m.receivedAt && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: 6 }}>· {fmtWhen(m.receivedAt)}</span>}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                          {[m.fromEmail, m.subject].filter(Boolean).join(' · ')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.summary}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0 }}>
                        <button style={primaryButtonStyle} onClick={() => setOpenOnlineMessage(m)}>Open</button>
                        <button style={secondaryButtonStyle} disabled={dismissOnlineMessage.isPending} onClick={() => dismissOnlineMessage.mutate(m.id)}>Dismiss</button>
                      </div>
                    </div>
                  ))}
                </div>
                {pendingTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)' }}>
                    <button
                      type="button"
                      aria-label="Previous page"
                      style={smallButtonStyle}
                      disabled={pendingPage <= 1}
                      onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                    >
                      ‹ Previous
                    </button>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                      Page {pendingPage} of {pendingTotalPages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      style={smallButtonStyle}
                      disabled={pendingPage >= pendingTotalPages}
                      onClick={() => setPendingPage((p) => Math.min(pendingTotalPages, p + 1))}
                    >
                      Next ›
                    </button>
                  </div>
                )}
              </>
            )
          ) : dismissedTotal === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No dismissed messages yet.</p>
          ) : (
            <>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
                {onlineDismissed.map((m, i) => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-3)', padding: 'var(--spacing-3)', borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', opacity: 0.75, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 240, flex: '1 1 320px' }}>
                      <div style={{ fontWeight: 700 }}>
                        {m.fromName ?? 'Unknown'}
                        <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', background: 'var(--color-surface-elevated)', borderRadius: 4, padding: '1px 6px' }}>Dismissed</span>
                        {m.receivedAt && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: 6 }}>· {fmtWhen(m.receivedAt)}</span>}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                        {[m.fromEmail, m.subject].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <button style={secondaryButtonStyle} onClick={() => setOpenOnlineMessage(m)}>Open</button>
                  </div>
                ))}
              </div>
              {dismissedTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)' }}>
                  <button
                    type="button"
                    aria-label="Previous page"
                    style={smallButtonStyle}
                    disabled={dismissedPage <= 1}
                    onClick={() => setDismissedPage((p) => Math.max(1, p - 1))}
                  >
                    ‹ Previous
                  </button>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    Page {dismissedPage} of {dismissedTotalPages}
                  </span>
                  <button
                    type="button"
                    aria-label="Next page"
                    style={smallButtonStyle}
                    disabled={dismissedPage >= dismissedTotalPages}
                    onClick={() => setDismissedPage((p) => Math.min(dismissedTotalPages, p + 1))}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {openOnlineMessage != null && (
        <OnlineMessageModal
          message={openOnlineMessage}
          onClose={() => setOpenOnlineMessage(null)}
          onDismiss={() => dismissOnlineMessage.mutate(openOnlineMessage.id)}
          dismissing={dismissOnlineMessage.isPending}
        />
      )}

      {view === 'contacts' ? (
        <>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, mobile, company…"
            style={{ ...inputStyle, maxWidth: 360 }}
          />

          <section style={pageCardStyle}>
        <ResponsiveList
          rows={isLoading ? [] : contacts}
          getRowKey={(c) => String(c.id)}
          emptyMessage={isLoading ? 'Loading…' : 'No contacts found.'}
          columns={[
            {
              key: 'name',
              label: 'Name',
              primaryOnMobile: true,
              render: (c) => (
                <button
                  onClick={() => openDetails(c.id)}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                >
                  {c.fullName ?? '—'}
                </button>
              ),
            },
            { key: 'email', label: 'Email', render: (c) => c.email ?? '—' },
            { key: 'mobile', label: 'Mobile', render: (c) => c.mobile ?? '—' },
            { key: 'company', label: 'Company', render: (c) => c.company ?? '—', hideOnMobile: true },
            {
              key: 'source',
              label: 'Source',
              render: (c) =>
                c.source === 'csv'
                  ? badge('CSV', 'var(--color-text-muted)', 'var(--color-surface-elevated)')
                  : badge(c.source ?? 'manual', 'var(--color-text-muted)', 'var(--color-surface-elevated)'),
            },
            {
              key: 'xero',
              label: 'Xero',
              render: (c) =>
                !c.inXero
                  ? badge('Not in Xero', 'var(--color-text-muted)', 'var(--color-surface-elevated)')
                  : c.differsFromXero
                    ? badge('Differs', 'var(--color-error)', 'rgba(220,53,69,0.12)')
                    : badge('Matches', 'var(--color-success)', 'rgba(40,167,69,0.12)'),
            },
            {
              key: 'action',
              label: '',
              align: 'right',
              render: (c) => (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                  {c.mobile && (
                    <button
                      style={secondaryButtonStyle}
                      title="Open this contact's details and SMS conversation"
                      onClick={() => openDetails(c.id)}
                    >
                      Send SMS
                    </button>
                  )}
                  <button style={secondaryButtonStyle} onClick={() => openDetails(c.id)}>
                    Details
                  </button>
                  <button style={primaryButtonStyle} onClick={() => openChat(c.id)}>
                    Open chat
                  </button>
                </div>
              ),
            },
          ]}
        />
      </section>
        </>
      ) : null}

      {/* Details modal — fields, Xero compare, edit & delete */}
      {detailId != null && detail && (
        <Modal size="full-page" onClose={() => setDetailId(null)}>
          <h2 style={{ marginTop: 0 }}>{detail.customer.full_name ?? 'Contact'}</h2>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Source: <strong>{detail.customer.source}</strong> ·{' '}
            {detail.inXero ? `Linked to Xero${detail.xero?.name ? `: ${detail.xero.name}` : ''}` : 'Not in Xero'}</span>
            {detail.inXero ? (
              <>
                <button
                  style={{ ...secondaryButtonStyle, minHeight: 28, padding: '2px 10px', fontSize: 'var(--font-size-xs)' }}
                  onClick={() => setXeroLinkFor({ id: detail.customer.id, name: detail.customer.full_name || 'this contact' })}
                >
                  Change Xero link
                </button>
                <button
                  style={{ ...secondaryButtonStyle, minHeight: 28, padding: '2px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                  onClick={() => setPendingUnlink({ id: detail.customer.id, name: detail.customer.full_name || 'this contact' })}
                >
                  Unlink
                </button>
              </>
            ) : (
              <button
                style={{ ...primaryButtonStyle, minHeight: 28, padding: '2px 10px', fontSize: 'var(--font-size-xs)' }}
                onClick={() => setXeroLinkFor({ id: detail.customer.id, name: detail.customer.full_name || 'this contact' })}
              >
                🔗 Link to Xero
              </button>
            )}
          </p>
          {detail.subscriptions && (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'center' }}>
              {(['email', 'sms'] as const).map((ch) => {
                const status = detail.subscriptions[ch] as 'subscribed' | 'unsubscribed' | 'none';
                const label = ch === 'email' ? 'Email' : 'SMS';
                const color =
                  status === 'subscribed' ? 'var(--color-success)' : status === 'unsubscribed' ? 'var(--color-error)' : 'var(--color-text-muted)';
                const bg =
                  status === 'subscribed' ? 'rgba(40,167,69,0.12)' : status === 'unsubscribed' ? 'rgba(220,53,69,0.12)' : 'rgba(108,117,125,0.12)';
                const disabled = status === 'none' || toggleSub.isPending;
                return (
                  <button
                    key={ch}
                    disabled={disabled}
                    title={
                      status === 'none'
                        ? `No ${ch === 'email' ? 'email' : 'mobile'} on file`
                        : status === 'subscribed'
                          ? 'Click to unsubscribe'
                          : 'Click to subscribe'
                    }
                    onClick={() => toggleSub.mutate({ id: detail.customer.id, channel: ch, optedOut: status === 'subscribed' })}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color,
                      backgroundColor: bg,
                      border: `1px solid ${color}`,
                      borderRadius: 4,
                      padding: '2px 8px',
                      cursor: disabled ? 'default' : 'pointer',
                    }}
                  >
                    {label}: {status}
                    {status !== 'none' ? ' ✎' : ''}
                  </button>
                );
              })}
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>(click to change)</span>
            </div>
          )}
          {/* Arrival-reminder channel: how the "we're on the way" reminder reaches
              this customer. Default (Auto) = SMS, falling back to email when there's
              no mobile. Force SMS or Email for customers with a preference. */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Arrival reminder:</span>
            {([
              { key: null, label: 'Auto' },
              { key: 'sms', label: 'SMS' },
              { key: 'email', label: 'Email' },
            ] as const).map((opt) => {
              const current = (detail.customer.arrival_channel ?? null) as 'sms' | 'email' | null;
              const active = current === opt.key;
              return (
                <button
                  key={opt.label}
                  disabled={setArrivalChannel.isPending || active}
                  title={
                    opt.key === null
                      ? 'Auto: send by SMS, or email if there is no mobile on file'
                      : opt.key === 'sms'
                        ? 'Always send the arrival reminder by SMS'
                        : 'Always send the arrival reminder by email'
                  }
                  onClick={() => setArrivalChannel.mutate({ id: detail.customer.id, channel: opt.key })}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: active ? 'var(--color-surface)' : 'var(--color-text)',
                    backgroundColor: active ? 'var(--color-accent)' : 'transparent',
                    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: 4,
                    padding: '2px 10px',
                    cursor: active || setArrivalChannel.isPending ? 'default' : 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
            {(detail.customer.arrival_channel ?? null) === null && !detail.customer.mobile && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                (no mobile — currently emails)
              </span>
            )}
          </div>
          {detail.customer.removed_at && (
            <div
              style={{
                background: 'rgba(108,117,125,0.12)',
                border: '1px solid var(--color-text-muted, #6c757d)',
                borderRadius: 'var(--radius-md, 8px)',
                padding: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              🚫 Removed on{' '}
              {new Date(detail.customer.removed_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })}
              {detail.customer.removed_source ? ` (${detail.customer.removed_source})` : ''}
              {detail.customer.removed_reason ? ` — ${detail.customer.removed_reason}` : ''}. Excluded from all
              campaigns. Use Restore to undo.
            </div>
          )}
          {Array.isArray(detail.bounces) && detail.bounces.length > 0 && (
            <div
              style={{
                background: 'rgba(220,53,69,0.10)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-md, 8px)',
                padding: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {detail.bounces.map((b: any, i: number) => (
                <div key={i}>
                  ⚠ <strong>{b.email}</strong> hard-bounced
                  {b.code ? ` (${b.code})` : ''} on{' '}
                  {new Date(b.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })} and was
                  removed from this contact, so campaigns won't email it again. It may still appear in the Xero column below.
                </div>
              ))}
            </div>
          )}
          <details style={{ marginBottom: 'var(--spacing-3)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: 0.4, padding: 'var(--spacing-1) 0' }}>
              Fields (Local vs Xero)
            </summary>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 'var(--spacing-2)' }}>
              <thead>
                <tr>
                  {['Field', 'Local', 'Xero', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border)', padding: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.fields.map((f: any) => (
                  <tr key={f.key}>
                    <td style={{ padding: 'var(--spacing-2)', fontWeight: 600 }}>{f.key}</td>
                    <td style={{ padding: 'var(--spacing-2)' }}>{f.local ?? '—'}</td>
                    <td style={{ padding: 'var(--spacing-2)' }}>{f.xero ?? '—'}</td>
                    <td style={{ padding: 'var(--spacing-2)' }}>
                      {f.differs
                        ? badge('differs', 'var(--color-error)', 'rgba(220,53,69,0.12)')
                        : f.onlyInXero
                          ? badge('Xero only', 'var(--color-accent)', 'rgba(212,175,55,0.12)')
                          : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          {/* Campaigns this contact is part of — click to open the campaign. */}
          {Array.isArray(detail.campaigns) && detail.campaigns.length > 0 && (
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <h3 style={{ margin: '0 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                Campaigns ({detail.campaigns.length})
              </h3>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 8px)', overflow: 'hidden' }}>
                {detail.campaigns.map((cp: any, i: number) => (
                  <div
                    key={`${cp.campaignId}-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      padding: 'var(--spacing-2)',
                      borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                    }}
                  >
                    <button
                      onClick={() => navigate(`/campaigns?open=${cp.campaignId}`)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                    >
                      {cp.campaignName} ↗
                    </button>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {cp.recipientStatus}
                      {cp.sentAt ? ` · ${new Date(cp.sentAt).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full history: recent emails + Xero quotes/invoices for this contact. */}
          <ContextPanel detail={detail} />

          {/* Call this contact via the softphone. */}
          {detail.customer.mobile && (
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-3)', marginTop: 'var(--spacing-2)' }}>
              <button
                style={{ ...primaryButtonStyle, background: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
                onClick={() => { setPhonePrefill(detail.customer.mobile ?? undefined); setView('phone'); setDetailId(null); }}
              >
                📞 Call {detail.customer.mobile}
              </button>
            </div>
          )}

          {/* SMS conversation with this contact — message history + composer, in one place. */}
          {detail.customer.mobile && (
            <div style={{ marginBottom: 'var(--spacing-3)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-3)' }}>
              <SmsConversation phone={detail.customer.mobile} customerId={detail.customer.id} contactName={detail.customer.full_name} />
            </div>
          )}

          {partnerNote && <p style={{ textAlign: 'right', color: 'var(--color-success)', fontSize: 'var(--font-size-sm)', margin: '0 0 var(--spacing-2)' }}>{partnerNote}</p>}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {(() => {
              const name = detail.customer.full_name || detail.customer.email || '';
              const q = encodeURIComponent(name);
              const xid = detail.customer.xero_contact_id;
              if (!q) return null;
              // Quotes are matched to a contact by Xero id (a quote can be under a
              // different display name), so link by xero id when we have it.
              const quotesHref = xid
                ? `/billing?tab=quotes&xeroContact=${encodeURIComponent(xid)}&label=${q}`
                : `/billing?tab=quotes&q=${q}`;
              return (
                <>
                  <button style={secondaryButtonStyle} onClick={() => navigate(quotesHref)}>🧾 Quotes</button>
                  <button style={secondaryButtonStyle} onClick={() => navigate(`/calendar?customer=${q}`)}>📅 Jobs</button>
                </>
              );
            })()}
            <button
              style={secondaryButtonStyle}
              disabled={addAsPartner.isPending}
              title="Add this contact to the referral partners list"
              onClick={() => addAsPartner.mutate({ name: detail.customer.full_name || detail.customer.email || 'Partner', email: detail.customer.email ?? null, mobile: detail.customer.mobile ?? null })}
            >
              {addAsPartner.isPending ? 'Adding…' : '🤝 Add as partner'}
            </button>
            {detail.customer.removed_at ? (
              <button
                style={secondaryButtonStyle}
                disabled={restoreMutation.isPending}
                onClick={() => restoreMutation.mutate(detail.customer.id)}
              >
                {restoreMutation.isPending ? 'Restoring…' : 'Restore customer'}
              </button>
            ) : (
              <button
                style={secondaryButtonStyle}
                onClick={() => setPendingRemove({ id: detail.customer.id, name: detail.customer.full_name })}
              >
                Remove customer
              </button>
            )}
            <button
              style={dangerButtonStyle}
              onClick={() => setPendingDelete({ id: detail.customer.id, name: detail.customer.full_name })}
            >
              Delete
            </button>
            <button
              style={secondaryButtonStyle}
              onClick={() =>
                setEditing({
                  id: detail.customer.id,
                  full_name: detail.customer.full_name ?? '',
                  email: detail.customer.email ?? '',
                  mobile: detail.customer.mobile ?? '',
                  company: detail.customer.company ?? '',
                  address_1: detail.customer.address_1 ?? '',
                  suburb: detail.customer.suburb ?? '',
                })
              }
            >
              Edit
            </button>
            <button style={secondaryButtonStyle} onClick={() => openChat(detail.customer.id)}>
              Open chat ({detail.notes?.length ?? 0})
            </button>
            <button style={primaryButtonStyle} onClick={() => setDetailId(null)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Chat modal — full customer context + notes / stories thread + composer */}
      {chatId != null && detail && (
        <Modal size="full" onClose={() => setChatId(null)}>
          <h2 style={{ marginTop: 0 }}>{detail.customer.full_name ?? 'Contact'}</h2>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
            {detail.inXero ? 'Linked to Xero' : 'Not in Xero'}
            {detail.customer.email ? ` · ${detail.customer.email}` : ''}
            {detail.customer.mobile ? ` · ${detail.customer.mobile}` : ''}
          </p>

          <ContextPanel detail={detail} />

          {/* Conversation thread — oldest at top, newest at the bottom */}
          <div
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-surface)',
              padding: 'var(--spacing-3)',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {(detail.notes ?? []).length === 0 && convo.length === 0 && (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', fontSize: 'var(--font-size-sm)', margin: 'var(--spacing-4) 0' }}>
                Ask the assistant anything — e.g. “Quote a single-storey house softwash + windows”,
                “What’s outstanding on their invoices?”, or “Draft a follow-up”. It uses the quoting
                methodology, the service rates and this customer’s history.
              </p>
            )}

            {/* Saved history (logged notes + photos) */}
            {[...(detail.notes ?? [])].reverse().map((n: any) => (
              <div key={n.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--spacing-3)' }}>
                <div style={{ maxWidth: '82%' }}>
                  <div
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--color-accent)',
                      color: 'var(--color-text-inverse)',
                    }}
                  >
                    <div style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>
                      {n.summary || n.raw_message}
                    </div>
                    {n.attachments?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
                        {n.attachments.map((a: any) => (
                          <AuthImage key={a.id} id={a.id} filename={a.filename} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2, textAlign: 'right' }}>
                    {n.category} · {new Date(n.created_at).toLocaleDateString('en-AU')}
                  </div>
                </div>
              </div>
            ))}

            {/* Live assistant conversation */}
            {convo.map((t, i) => (
              <div
                key={`c${i}`}
                style={{ display: 'flex', justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 'var(--spacing-3)' }}
              >
                <div style={{ maxWidth: '82%' }}>
                  <div
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderRadius: 'var(--radius-lg)',
                      background: t.role === 'user' ? 'var(--color-accent)' : 'var(--color-surface-elevated)',
                      color: t.role === 'user' ? 'var(--color-text-inverse)' : 'var(--color-text)',
                      border: t.role === 'user' ? 'none' : '1px solid var(--color-border)',
                      whiteSpace: 'pre-wrap',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    {t.content}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2, textAlign: t.role === 'user' ? 'right' : 'left' }}>
                    {t.role === 'user' ? 'You' : 'Assistant'}
                  </div>
                  {t.role === 'assistant' && t.contextUsed && (
                    <details style={{ marginTop: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      <summary style={{ cursor: 'pointer', userSelect: 'none' }}>🔎 Context used</summary>
                      <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid var(--color-border)' }}>
                        <div>
                          📚 Knowledge base:{' '}
                          {t.contextUsed.knowledgeBase
                            .map((s) => s.replace(/-/g, ' '))
                            .join(', ')}
                        </div>
                        {t.contextUsed.customer && (
                          <div style={{ marginTop: 2 }}>
                            👤 {t.contextUsed.customer.name || 'this contact'}:{' '}
                            {t.contextUsed.customer.counts.quotes} quotes ·{' '}
                            {t.contextUsed.customer.counts.invoices} invoices ·{' '}
                            {t.contextUsed.customer.counts.bookings} bookings ·{' '}
                            {t.contextUsed.customer.counts.notes} notes
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                  {t.role === 'assistant' && !t.content.startsWith('⚠️') && (
                    <div style={{ marginTop: 4, fontSize: 'var(--font-size-xs)' }}>
                      {chatRated[i] ? (
                        <span style={{ color: 'var(--color-text-muted)' }}>{chatRated[i] === 'good' ? '👍 Thanks, noted.' : '👎 Noted, it will learn from this.'}</span>
                      ) : chatReject?.i === i ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
                          <input
                            autoFocus
                            value={chatReject.text}
                            onChange={(e) => setChatReject({ i, text: e.target.value })}
                            placeholder="What's wrong / the correct answer?"
                            style={{ ...inputStyle, fontSize: 'var(--font-size-xs)', padding: '2px 6px', width: 260 }}
                          />
                          <button
                            style={{ ...secondaryButtonStyle, padding: '2px 8px' }}
                            disabled={chatFbMut.isPending || !chatReject.text.trim()}
                            onClick={() => chatFbMut.mutate({ i, verdict: 'bad', question: convo[i - 1]?.content ?? '', answer: t.content, note: chatReject.text.trim() })}
                          >
                            Submit
                          </button>
                        </div>
                      ) : (
                        <span>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 8 }} title="Good answer" onClick={() => chatFbMut.mutate({ i, verdict: 'good', question: convo[i - 1]?.content ?? '', answer: t.content })}>👍</button>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title="Wrong — tell it why" onClick={() => setChatReject({ i, text: '' })}>👎</button>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {askMutation.isPending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 'var(--spacing-3)' }}>
                <div style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderRadius: 'var(--radius-lg)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  Assistant is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer — ask the assistant (Send) or log a note/photo (Save note) */}
          {files.length > 0 && (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>
              {files.length} image(s) attached — use “Save note” to log them to this customer.
            </div>
          )}
          {understandMut.isPending && !understanding && (
            <div style={{ marginBottom: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              🔎 Reading the message…
            </div>
          )}
          {understanding && (
            <div style={{ marginBottom: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', padding: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', background: 'var(--color-surface-elevated)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>🔎 Read: {understanding.intent.replace(/_/g, ' ')} · {understanding.confidence} confidence</strong>
                <button onClick={() => setUnderstanding(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}>✕</button>
              </div>
              <div style={{ marginTop: 4 }}>{understanding.summary}</div>
              {understanding.references.quotes.length > 0 && (
                <div style={{ marginTop: 2 }}>📄 {understanding.references.quotes.map((q) => `${q.number}${q.found ? ` (${q.status})` : ' (not found)'}`).join(', ')}</div>
              )}
              {understanding.references.addresses.length > 0 && (
                <div style={{ marginTop: 2 }}>📍 {understanding.references.addresses.join(' · ')}</div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
            <label
              style={{ ...secondaryButtonStyle, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Attach images to log to this customer"
            >
              📎
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                style={{ display: 'none' }}
              />
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  askAssistant();
                }
              }}
              placeholder="Ask the assistant…  (⌘/Ctrl+Enter to send)"
              rows={2}
              style={{
                flex: 1,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                resize: 'vertical',
                fontFamily: 'inherit',
                minHeight: 44,
              }}
            />
            <button
              style={secondaryButtonStyle}
              title="Log this text and any attached photos to the customer's history"
              disabled={sendNoteMutation.isPending || (!noteText.trim() && files.length === 0)}
              onClick={() => sendNoteMutation.mutate()}
            >
              {sendNoteMutation.isPending ? '…' : 'Save note'}
            </button>
            <button
              style={primaryButtonStyle}
              title="Ask the assistant (uses quoting methodology, rates and this customer's history)"
              disabled={askMutation.isPending || !noteText.trim()}
              onClick={askAssistant}
            >
              {askMutation.isPending ? '…' : 'Send'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
            <button style={secondaryButtonStyle} onClick={() => openDetails(detail.customer.id)}>
              View details
            </button>
            <button style={primaryButtonStyle} onClick={() => setChatId(null)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Add / edit form modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <h2 style={{ marginTop: 0 }}>{editing.id ? 'Edit contact' : 'Add contact'}</h2>
          {(['full_name', 'email', 'mobile', 'company', 'address_1', 'suburb'] as const).map((k) => (
            <div key={k}>
              <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                {k.replace('_', ' ')}
              </label>
              <input
                style={inputStyle}
                value={editing[k] ?? ''}
                onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
              />
            </div>
          ))}
          {saveMutation.isError && (
            <div style={{ marginTop: 'var(--spacing-2)' }}>
              <p style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-2)' }}>{(saveMutation.error as Error).message}</p>
              {(() => {
                const data = (saveMutation.error as any)?.data;
                if (!data?.conflictId || !editing?.id) return null;
                return (
                  <button
                    style={{ ...primaryButtonStyle, background: 'var(--color-accent)' }}
                    disabled={mergeMutation.isPending}
                    onClick={() => { if (confirm(`Merge "${data.conflictName}" into this contact? Their bookings, messages and history move here, and the duplicate is removed. This can't be undone.`)) mergeMutation.mutate({ keepId: editing.id, mergeId: data.conflictId }); }}
                  >
                    {mergeMutation.isPending ? 'Merging…' : `🔗 Merge "${data.conflictName}" into this contact`}
                  </button>
                );
              })()}
              {mergeMutation.isError && <p style={{ color: 'var(--color-error)', marginTop: 'var(--spacing-1)' }}>{(mergeMutation.error as Error).message}</p>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
            <button style={secondaryButtonStyle} onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button
              style={primaryButtonStyle}
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(editing)}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title={`Delete ${pendingDelete?.name ?? 'contact'}?`}
        message="This permanently removes the contact from the portal (Xero is not affected)."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
      />
      <ConfirmDialog
        isOpen={!!pendingRemove}
        title={`Remove ${pendingRemove?.name ?? 'customer'}?`}
        message="Marks them as removed and stops all email + SMS campaigns. Their record and history are kept, and you can Restore them later."
        tone="warning"
        confirmLabel="Remove"
        onConfirm={() => pendingRemove && removeMutation.mutate(pendingRemove.id)}
        onCancel={() => setPendingRemove(null)}
      />
      <ConfirmDialog
        isOpen={!!pendingUnlink}
        title={`Unlink ${pendingUnlink?.name ?? 'contact'} from Xero?`}
        message="Their Xero quotes and invoices will stop showing under this contact. Nothing in Xero changes — you can re-link at any time."
        tone="warning"
        confirmLabel="Unlink"
        onConfirm={() => pendingUnlink && unlinkXero.mutate(pendingUnlink.id)}
        onCancel={() => setPendingUnlink(null)}
      />
      {xeroLinkFor && (
        <XeroLinkModal
          contactId={xeroLinkFor.id}
          contactName={xeroLinkFor.name}
          onClose={() => setXeroLinkFor(null)}
          onLinked={() => {
            setXeroLinkFor(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

/**
 * Search Xero contacts and link the chosen one to a local contact. Once linked,
 * all that Xero contact's quotes + invoices show under the local contact. A Xero
 * contact already linked elsewhere is shown but disabled (one-to-one).
 */
function XeroLinkModal({
  contactId,
  contactName,
  onClose,
  onLinked,
}: {
  contactId: number;
  contactName: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['xero-contact-search', debounced],
    queryFn: () => api.searchXeroContacts(debounced),
    enabled: debounced.length >= 2,
  });

  const link = useMutation({
    mutationFn: (xeroContactId: string) => api.linkContactXero(contactId, xeroContactId),
    onSuccess: () => onLinked(),
    onError: (e: any) => setError(e?.message || 'Could not link this contact.'),
  });

  return (
    <Modal size="large" onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>Link {contactName} to Xero</h3>
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0, fontSize: 'var(--font-size-sm)' }}>
        Find this contact in Xero. Linking pulls in all of their quotes and invoices automatically.
      </p>
      <input
        autoFocus
        style={inputStyle}
        placeholder="Search Xero by name, email, mobile or quote/invoice no.…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setError(null); }}
      />
      {error && (
        <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>{error}</p>
      )}
      <div style={{ marginTop: 'var(--spacing-3)', maxHeight: '50vh', overflowY: 'auto' }}>
        {debounced.length < 2 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Type at least 2 characters.</p>
        ) : isFetching ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Searching…</p>
        ) : results.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>No Xero contacts match “{debounced}”.</p>
        ) : (
          results.map((r: any) => {
            const takenElsewhere = r.linkedCustomerId && r.linkedCustomerId !== contactId;
            return (
              <div
                key={r.xeroContactId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderBottom: '1px solid var(--color-border)',
                  opacity: takenElsewhere ? 0.6 : 1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{r.name || '(no name)'}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', overflowWrap: 'anywhere' }}>
                    {[r.email, r.mobile].filter(Boolean).join(' · ') || 'No email/mobile in Xero'}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {r.quoteCount} quote{r.quoteCount === 1 ? '' : 's'} · {r.invoiceCount} invoice{r.invoiceCount === 1 ? '' : 's'}
                    {takenElsewhere ? ` · already linked to ${r.linkedCustomerName || 'another contact'}` : ''}
                  </div>
                  {(r.matchedQuote || r.matchedInvoice) && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)', fontWeight: 600 }}>
                      matches {r.matchedQuote || r.matchedInvoice}
                    </div>
                  )}
                </div>
                <button
                  style={{ ...primaryButtonStyle, minHeight: 32, padding: '4px 12px', flexShrink: 0 }}
                  disabled={link.isPending || takenElsewhere}
                  title={takenElsewhere ? 'This Xero contact is already linked to another contact' : 'Link this Xero contact'}
                  onClick={() => { setError(null); link.mutate(r.xeroContactId); }}
                >
                  {r.linkedCustomerId === contactId ? 'Linked' : 'Link'}
                </button>
              </div>
            );
          })
        )}
      </div>
      <div style={{ marginTop: 'var(--spacing-3)', textAlign: 'right' }}>
        <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Fetches an attachment with auth and renders it as a thumbnail (object URL).
function AuthImage({ id, filename }: { id: number; filename?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let made: string | null = null;
    api
      .attachmentObjectUrl(id)
      .then((u) => {
        made = u;
        if (active) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [id]);
  if (!url)
    return (
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        loading…
      </span>
    );
  return (
    <a href={url} target="_blank" rel="noreferrer" title={filename}>
      <img
        src={url}
        alt={filename ?? 'attachment'}
        style={{ height: 72, width: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)' }}
      />
    </a>
  );
}

function money(total: any, currency?: string): string {
  const n = Number(total);
  if (!isFinite(n)) return '—';
  return `${currency && currency !== 'AUD' ? currency + ' ' : '$'}${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function shortDate(d: any): string {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-AU');
}

/** Date + time in Brisbane, for "when received". */
function fmtWhen(d: any): string {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Deep link to a Xero quote/invoice by its document id (GUID). */
function xeroDocUrl(kind: 'quote' | 'invoice', xeroId?: string): string | null {
  if (!xeroId) return null;
  return kind === 'quote'
    ? `https://go.xero.com/app/quotes/view/${xeroId}`
    : `https://go.xero.com/app/invoicing/view/${xeroId}`;
}

/** A quote/invoice number that links to Xero when we have the document id. */
function XeroDocNumber({ kind, row }: { kind: 'quote' | 'invoice'; row: any }) {
  const url = xeroDocUrl(kind, row.xero_id);
  const label = row.number ?? '—';
  if (!url) return <>{label}</>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open in Xero ↗"
      style={{ color: 'var(--color-accent)', fontWeight: 600, textDecoration: 'none' }}
    >
      {label} ↗
    </a>
  );
}

// 360° context for a contact: snapshot stats + Xero quotes/invoices + emails.
function ContextPanel({ detail }: { detail: any }) {
  const quotes: any[] = detail.quotes ?? [];
  const invoices: any[] = detail.invoices ?? [];
  const emails: any[] = detail.emails ?? [];
  const stat = (label: string, value: string) => (
    <div style={{ flex: '1 1 120px' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );
  const titleStyle: React.CSSProperties = {
    margin: 'var(--spacing-3) 0 var(--spacing-1)',
    fontSize: 'var(--font-size-xs)',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    letterSpacing: 0.4,
  };
  const lineStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)',
    fontSize: 'var(--font-size-sm)',
    padding: '2px 0',
    borderBottom: '1px solid var(--color-border)',
  };

  return (
    <details
      style={{
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-default)',
        padding: 'var(--spacing-3)',
        marginBottom: 'var(--spacing-3)',
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
        Activity &amp; Xero
      </summary>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-3)' }}>
        {stat('Last booked', detail.lastBookingDate ? shortDate(detail.lastBookingDate) : '—')}
        {stat('Last appointment', detail.lastAppointmentDate ? shortDate(detail.lastAppointmentDate) : '—')}
        {stat('Appointments', String(detail.appointmentCount ?? 0))}
        {stat('Quotes', String(quotes.length))}
        {stat('Invoices', String(invoices.length))}
      </div>

      {quotes.length > 0 && (
        <>
          <div style={titleStyle}>Quotes</div>
          {quotes.slice(0, 6).map((q) => (
            <div key={q.xero_id} style={lineStyle}>
              <span><XeroDocNumber kind="quote" row={q} /> · {q.status} {q.doc_date ? `· ${shortDate(q.doc_date)}` : ''}</span>
              <span style={{ fontWeight: 600 }}>{money(q.total, q.currency)}</span>
            </div>
          ))}
        </>
      )}

      {invoices.length > 0 && (
        <>
          <div style={titleStyle}>Invoices</div>
          {invoices.slice(0, 6).map((iv) => (
            <div key={iv.xero_id} style={lineStyle}>
              <span><XeroDocNumber kind="invoice" row={iv} /> · {iv.status} {iv.doc_date ? `· ${shortDate(iv.doc_date)}` : ''}</span>
              <span style={{ fontWeight: 600 }}>{money(iv.total, iv.currency)}</span>
            </div>
          ))}
        </>
      )}

      {emails.length > 0 && (
        <>
          <div style={titleStyle}>Recent emails</div>
          {emails.slice(0, 6).map((e) => (
            <div key={e.gmail_id} style={{ ...lineStyle, display: 'block' }}>
              <div style={{ fontWeight: 600 }}>{e.subject || '(no subject)'}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {e.from_name || e.from_email} · {shortDate(e.received_at)}
              </div>
              {e.snippet && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{e.snippet}</div>
              )}
            </div>
          ))}
        </>
      )}

      {quotes.length === 0 && invoices.length === 0 && emails.length === 0 && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 'var(--spacing-2) 0 0' }}>
          No Xero quotes/invoices or emails on file yet{detail.inXero ? '' : ' — this contact isn’t linked to Xero'}.
        </p>
      )}
    </details>
  );
}

function Modal({
  children,
  onClose,
  size = 'default',
}: {
  children: React.ReactNode;
  onClose: () => void;
  size?: 'default' | 'large' | 'full' | 'full-page';
}) {
  const full = size === 'full';
  const fullPage = size === 'full-page'; // near-full-screen but scrolls the whole modal
  const width = full ? '96vw' : fullPage ? '98vw' : size === 'large' ? 'min(980px, 94vw)' : 'min(560px, 94vw)';
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: full || fullPage ? 'var(--spacing-2)' : 'var(--spacing-4)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...pageCardStyle,
          width,
          height: full || fullPage ? '96vh' : undefined,
          maxHeight: full || fullPage ? '96vh' : '88vh',
          overflowY: full ? 'hidden' : 'auto',
          display: full ? 'flex' : undefined,
          flexDirection: full ? 'column' : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Detail view for one website Quick Message (Contacts → Online). `subject`
 *  is repurposed to hold the phone number (see index.ts's /public/quick-message
 *  route — there's no dedicated phone column, mirroring how the Quote Tool
 *  route already repurposes `snippet` for image references). Dismiss is
 *  hidden once already dismissed (status !== 'new') — no restore needed,
 *  the record stays visible/openable in the Dismissed sub-view either way. */
function OnlineMessageModal({
  message,
  onClose,
  onDismiss,
  dismissing,
}: {
  message: AssistantObservation | null;
  onClose: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  if (!message) return null;
  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Quick Message</h2>
        <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
      </div>
      <div style={{ marginTop: 'var(--spacing-3)', display: 'grid', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
        <div><strong>Name:</strong> {message.fromName ?? '—'}</div>
        <div><strong>Phone:</strong> {message.subject ?? '—'}</div>
        <div><strong>Email:</strong> {message.fromEmail ?? '—'}</div>
        <div><strong>Received:</strong> {message.receivedAt ? fmtWhen(message.receivedAt) : '—'}</div>
      </div>
      <div style={{ marginTop: 'var(--spacing-3)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Message</div>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-3)' }}>
          {message.summary ?? '—'}
        </div>
      </div>
      {message.status === 'new' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-4)' }}>
          <button style={secondaryButtonStyle} disabled={dismissing} onClick={onDismiss}>
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      )}
    </Modal>
  );
}
